<template>
    <!--
            Bloc principal de vote (pouce haut / pouce bas).
            Le composant est masqué si la prop `show` vaut false.
        -->
    <div v-if="show">
        <!-- Conteneur des deux actions de feedback utilisateur -->
        <div class="d-flex gap-2" data-testid="thumb-vote-container">
            <!--
                            Bouton vote positif.
                            Désactivé si un vote a déjà été soumis
                        -->
            <button type="button"
                :class="['btn', 'btn-sm', 'btn-link', 'pl-0 pr-1', { 'vote-btn--disabled': isVoteDisabled }]"
                data-testid="thumb-up-btn" @click="onThumbUp" :disabled="isVoteDisabled"
                :aria-label="value === 'up' ? messages.thumbUpValidated : messages.thumbUpDefault"
                :title="value === 'up' ? messages.thumbUpValidated : messages.thumbUpDefault">
                <i :class="['bi', value === 'up' ? 'bi-hand-thumbs-up-fill' : 'bi-hand-thumbs-up', { 'vote-icon--disabled': isVoteDisabled }]"
                    :style="{ color: isVoteDisabled ? '#adb5bd' : '#198754', fontSize: '1.25rem' }"
                    aria-hidden="true"></i>
            </button>
            <!--
                            Bouton vote négatif.
                            Ouvre la modale de proposition si l'utilisateur est connecté,
                            sinon ouvre la popin de connexion.
                        -->
            <button type="button"
                :class="['btn', 'btn-sm', 'btn-link', 'pl-0', 'pr-1', { 'vote-btn--disabled': isVoteDisabled }]"
                data-testid="thumb-down-btn" @click="onThumbDown" :disabled="isVoteDisabled"
                :aria-label="messages.thumbDownTitle" :title="messages.thumbDownTitle">
                <i :class="['bi', value === 'down' ? 'bi-hand-thumbs-down-fill' : 'bi-hand-thumbs-down', { 'vote-icon--disabled': isVoteDisabled }]"
                    :style="{ color: isVoteDisabled ? '#adb5bd' : '#dc3545', fontSize: '1.25rem' }"
                    aria-hidden="true"></i>
            </button>
        </div>
        <!--
                    Modale de proposition d'une meilleure traduction (vote négatif enrichi).
                    Quand la proposition est soumise, le composant verrouille les boutons.
                -->
        <translator-proposal-modal :modal-id="modalId" :input-text="inputText" :translated-text="translatedText"
            :highlighted-terms="highlightedTerms" :translation-score="translationScore" :model-id="modelId"
            :direction="direction" :ordre="ordre" @proposal-submitted="markVoteAsSubmitted" />
    </div>
</template>

<script>
import translatorConfig from '../config/translator.config.js';
import TranslatorProposalModal from './TranslatorProposalModal.vue';
import storeTranslator from '../store-translator.js';

export default {
    // Composant de feedback utilisateur pour une traduction donnée.
    name: 'TranslatorThumbVoteOutput',
    components: { TranslatorProposalModal },
    props: {
        // Identifiant HTML de la modale de proposition.
        modalId: {
            type: String,
            default: 'modalProposerTraduction'
        },
        // État courant du vote : 'up', 'down' ou null.
        value: {
            type: String,
            default: null
        },
        // Permet de masquer totalement le composant.
        show: {
            type: Boolean,
            default: true
        },
        // Texte source saisi par l'utilisateur.
        inputText: {
            type: String,
            default: ''
        },
        // Texte traduit affiché en sortie.
        translatedText: {
            type: String,
            default: ''
        },
        // Termes mis en évidence dans le résultat.
        highlightedTerms: {
            type: Array,
            default: () => []
        },
        // Score de confiance associé à la traduction.
        translationScore: {
            type: Number,
            default: null
        },
        // Ordre de la traduction (utile pour les listes de résultats).
        ordre: {
            type: Number,
            default: 1
        },
        // Identifiant du modèle IA utilisé.
        modelId: {
            type: String,
            default: null
        },
        // Direction de traduction (ex: en_fr).
        direction: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            // Libellés UI centralisés dans la configuration.
            messages: translatorConfig.output.messages,
            // Snapshot de l'état de connexion au moment du montage.
            isUserLoggedIn: Boolean(window.ansPublicTranslatorAi && window.ansPublicTranslatorAi.isUserLoggedIn),
            // Empêche plusieurs soumissions successives pour un même item.
            hasSubmittedVote: false
        }
    },
    computed: {
        // Le vote est désactivé si déjà soumis ou déjà fixé par la prop `value`.
        isVoteDisabled() {
            return this.hasSubmittedVote || this.value !== null
        }
    },
    methods: {
        // Soumet un vote positif si autorisé, sinon ouvre la popin de connexion.
        async onThumbUp() {
            if (this.isVoteDisabled) return

            if (!this.isUserLoggedIn) {
                this.openLoginRequiredModal()
                return
            }

            await storeTranslator.soumettreVote({
                positive: true,
                inputText: this.inputText,
                translatedText: this.translatedText,
                ordre: this.ordre,
                modelId: this.modelId,
                direction: this.direction
            })
            this.markVoteAsSubmitted()
        },
        // Gère le vote négatif : ouverture de la popin de connexion ou de la modale.
        onThumbDown() {
            if (this.isVoteDisabled) return
            if (!this.isUserLoggedIn) {
                this.openLoginRequiredModal()
                return
            }
            this.openProposalModal()
        },
        // Verrouille les boutons après soumission d'un vote/proposition.
        markVoteAsSubmitted() {
            this.hasSubmittedVote = true
        },
        // Ouvre la popin de connexion (jQuery modal ou bootstrap natif).
        openLoginRequiredModal() {
            const el = document.getElementById('popin-login-required-translation-proposal')
            if (!el) return

            const jq = window.jQuery || window.$
            if (jq && jq.fn && jq.fn.modal) {
                jq(el).modal('show')
            } else if (window.bootstrap && window.bootstrap.Modal) {
                new window.bootstrap.Modal(el).show()
            }
        },
        // Ouvre la modale de proposition avec fond statique.
        openProposalModal() {
            const el = document.getElementById(this.modalId)
            if (!el) return
            const jq = window.jQuery || window.$
            if (jq && jq.fn && jq.fn.modal) {
                jq(el).modal({ backdrop: 'static', keyboard: false, show: true })
            } else if (window.bootstrap && window.bootstrap.Modal) {
                new window.bootstrap.Modal(el, { backdrop: 'static', keyboard: false }).show()
            }
        }
    }
}
</script>

<style scoped>
/* Supprime l'outline/focus ring par défaut sur les boutons de vote. */
[data-testid="thumb-up-btn"]:focus,
[data-testid="thumb-down-btn"]:focus {
    outline: none;
    box-shadow: none;
}


/* Style forcé des boutons désactivés pour conserver un rendu homogène. */
button.vote-btn--disabled[disabled],
button.vote-btn--disabled[disabled]:hover,
button.vote-btn--disabled[disabled]:focus,
button.vote-btn--disabled[disabled]:active {
    color: #adb5bd !important;
    opacity: 1 !important;
    background-color: transparent !important;
    border-color: transparent !important;
    cursor: not-allowed !important;
}

/* Applique la couleur grisée aux icônes quand le vote est verrouillé. */
button.vote-btn--disabled[disabled] .vote-icon--disabled {
    color: #adb5bd !important;
    cursor: not-allowed !important;
}
</style>
